import { useState } from "react";

import * as Yup from "yup";

import {
  achPaymentBaseSchema,
  MAX_MEMO_LENGTH,
} from "../../modules/achPayments/ACHPayment.form";

import type { FormikErrors } from "formik";
import type { TypeSafeReactLocalization } from "../../../composites";

export const MAX_BULK_TITLE_LENGTH = 80;

export type BasePaymentRowType = {
  // recipient is required when we create a bulk payment
  recipient?: API.Recipient | undefined;
  description: string;
  amount: number;
  transactionType: "send" | "collect";
};

export const basePaymentRowSchema = ({
  getString,
}: TypeSafeReactLocalization) =>
  Yup.object().shape({
    recipient: Yup.object().required(
      getString("bulk-ach-form-error-recipient"),
    ),
    description: Yup.string().max(
      128,
      getString("bulk-ach-form-error-description", { count: 128 }),
    ),
    amount: Yup.number()
      .positive(getString("form-generic-validation-positive-number"))
      .required(getString("bulk-ach-form-error-amount")),
  });

export const baseBulkFormSchema = (l10n: TypeSafeReactLocalization) =>
  achPaymentBaseSchema(l10n)
    .pick(["fromAccount"])
    .concat(
      Yup.object().shape({
        title: Yup.string()
          .required(l10n.getString("form-generic-validation-required"))
          .max(
            MAX_BULK_TITLE_LENGTH,
            l10n.getString("form-generic-validation-limit-characters", {
              count: MAX_BULK_TITLE_LENGTH,
            }),
          ),
        memo: Yup.string().max(MAX_MEMO_LENGTH),
        groupPosting: Yup.boolean().required(
          l10n.getString("form-generic-validation-required"),
        ),
      }),
    );

type BaseRowForm<T extends BasePaymentRowType> = {
  values: { payments: T[] };
  touched: { payments?: unknown[] };
  errors: { payments?: string | string[] | Array<FormikErrors<T>> };
  setFieldValue: (field: string, value: unknown) => void;
  setTouched: (touched: Record<string, unknown>) => void;
  setErrors: (errors: Record<string, unknown>) => void;
};

export const useBaseRowActions = <T extends BasePaymentRowType>(
  form: BaseRowForm<T>,
) => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const { values, setFieldValue, setTouched, setErrors } = form;

  const addRow = (newRow: T) => {
    setFieldValue("payments", [...values.payments, newRow]);
  };

  const clearRowsSelection = () => {
    setSelectedRows([]);
  };

  const removeRows = () => {
    const indicesSet = new Set(selectedRows);

    setFieldValue(
      "payments",
      values.payments.filter((_, i) => !indicesSet.has(i)),
    );

    const newTouchedPayments = Array.isArray(form.touched.payments)
      ? form.touched.payments.filter((_, i) => !indicesSet.has(i))
      : [];

    const newErrorPayments = Array.isArray(form.errors.payments)
      ? (form.errors.payments as Array<FormikErrors<T>>).filter(
          (_, i) => !indicesSet.has(i),
        )
      : [];

    setTouched({
      ...form.touched,
      payments: newTouchedPayments,
    });

    setErrors({
      ...form.errors,
      payments: newErrorPayments,
    });

    clearRowsSelection();
  };

  const toggleRowSelection = (index: number) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(index)
        ? prevSelected.filter((i) => i !== index)
        : [...prevSelected, index],
    );
  };

  const selectAllRows = () => {
    setSelectedRows(values.payments.map((_, index) => index));
  };

  const duplicateRow = () => {
    const index = selectedRows[0];
    if (index === undefined) return;
    const rowToDuplicate = values.payments[index];
    if (!rowToDuplicate) return;

    const newPayments = [...values.payments];
    newPayments.splice(index + 1, 0, { ...rowToDuplicate });

    setFieldValue("payments", newPayments);
  };

  const toggleAllRows = (toggleOn: boolean) => {
    if (toggleOn) {
      selectAllRows();
    } else {
      clearRowsSelection();
    }
  };

  return {
    addRow,
    duplicateRow,
    clearRowsSelection,
    selectAllRows,
    removeRows,
    toggleRowSelection,
    selectedRows,
    toggleAllRows,
  };
};
